import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Flex } from 'antd';
import { useFormik } from 'formik';

import {
  useAppSelector,
  useNavigationToListRequests,
  useRequisitionInfoExistingReq,
} from '@Hooks/index';
import { transformDictToOption } from '@Utils/transformToOptions';
import { parseDateTime } from '@Utils/transformDateTime';
import { createdUserRequestSelector } from '@Store/selectors/userInfoSelector';
import { getSalaryTypeDictSelector } from '@Store/selectors/dictionarySelector';
import { useRespondHrmRequestMutation } from '@Store/api/basicHrmApiSlice';
import {
  FormApproveRejectRequest,
  HrmRequestFormResponse,
} from '@Types/api/form';
import { NotificationContext } from '@Context/NotificationContext';
import { MainForm } from '@Components/index';
import FormItems from '@Components/MainForm/components/FormItems/FormItems';
import ApproveList from '@Components/MainForm/components/ApproveList/ApproveList';
import { FooterActions } from '@Enum/index';
import { Languages } from '@Features/EmployeeSelfService/requests/SalaryCertificate/languages';
import { ReassignWindow } from '@Components/ReassignWindow';
import { EMPTY_FIELD_PLACEHOLDER } from '@Constants';

import { initialValuesForm } from '../initialValuesForm';

interface SalaryCertificateApproveFormProps {
  formData: HrmRequestFormResponse;
  taskId: string;
}

export const SalaryCertificateApproveForm: React.FC<
  SalaryCertificateApproveFormProps
> = ({ formData, taskId }) => {
  const {
    taskApprovedNotification,
    taskErrorNotification,
    taskRejectedNotification,
    errorNotification,
  } = useContext(NotificationContext);
  const { handleBackToListRequests } = useNavigationToListRequests();
  const salaryTypes = useAppSelector(getSalaryTypeDictSelector);
  const requestedUser = useAppSelector(createdUserRequestSelector);
  const [postApproveRejectForm] = useRespondHrmRequestMutation();
  const requisitionInfo = useRequisitionInfoExistingReq(formData, 'Salary');

  const [comments, setCommentValue] = useState('');
  const [isReassignOpen, setReassignOpen] = useState(false);

  const formik = useFormik({
    initialValues: initialValuesForm,
    enableReinitialize: true,
    onSubmit: (): void => {},
  });

  useEffect(() => {
    if (formData) {
      formik.setValues({
        purpose: transformDictToOption(salaryTypes, formData.leaveTypeID).label,
        requiredByDate: parseDateTime(formData.startDate, 'DD/MM/YYYY'),
        language: formData.destination,
        remarks: formData.remarks,
      });
    }
  }, [formData]);

  const handleApproveReject = useCallback(
    (action: FooterActions.APPROVE | FooterActions.REJECT): void => {
      const body: FormApproveRejectRequest = {
        taskId: String(taskId),
        command: action,
        comment: comments,
      };

      if (action === FooterActions.REJECT && !comments) {
        errorNotification('Please leave a comment');
        return;
      }

      postApproveRejectForm(body)
        .unwrap()
        .then(() => {
          if (action === FooterActions.APPROVE) {
            taskApprovedNotification();
          } else {
            taskRejectedNotification();
          }

          handleBackToListRequests();
        })
        .catch(() => {
          taskErrorNotification();
        });
    },
    [comments, handleBackToListRequests, taskId]
  );

  const handleFooterAction = useCallback(
    (action: FooterActions, payload?: string) => {
      if (action === FooterActions.APPROVE || action === FooterActions.REJECT) {
        handleApproveReject(action);
      }

      if (action === FooterActions.COMMENTS_CHANGE) {
        setCommentValue(payload || '');
      }

      if (action === FooterActions.BACK_TO_REQUESTS) {
        handleBackToListRequests();
      }

      if (action === FooterActions.REASSIGN) {
        setReassignOpen(true);
      }
    },
    [handleBackToListRequests, handleApproveReject]
  );

  const dataSourceRequestDetails = useMemo(() => {
    const language =
      Languages[formik.values.language as keyof typeof Languages];

    const output = [
      {
        label: 'Purpose',
        value: formik.values.purpose || '',
      },
      {
        label: 'Remarks',
        value: formik.values.remarks || EMPTY_FIELD_PLACEHOLDER,
      },
      {
        label: 'Required Language',
        value: language,
      },
      {
        label: 'Required By',
        value: formik.values.requiredByDate,
      },
    ];
    return output;
  }, [formik.values]);

  return (
    <Flex gap={10} vertical style={{ height: '100%' }}>
      <MainForm
        formType="approval"
        handleSubmit={formik.handleSubmit}
        userInfo={requestedUser}
        requisitionInfo={requisitionInfo}
        onFooterAction={handleFooterAction}
        showExtraUserFields
      >
        <FormItems>
          <ApproveList
            dataSource={dataSourceRequestDetails}
            label="Request Details"
          />
        </FormItems>
      </MainForm>
      <ReassignWindow
        creatorRequestNumber={requestedUser?.empNumber}
        taskId={taskId}
        isOpen={isReassignOpen}
        onClose={() => {
          setReassignOpen(false);
        }}
        onUserSelected={() => {
          setReassignOpen(false);
          handleBackToListRequests();
        }}
      />
    </Flex>
  );
};

export default SalaryCertificateApproveForm;
